import PageHeader from "@/components/common/PageHeader";
import { backgroundDbWorkRefetchIntervalMs } from "@/lib/background-db-work-pause";
import { monitoringService } from "@/services/monitoring.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color = "slate",
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) {
  const colorMap: Record<string, string> = {
    green: "text-green-600",
    red: "text-red-600",
    orange: "text-orange-600",
    blue: "text-blue-600",
    slate: "text-slate-900",
  };

  return (
    <Card>
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
        {label}
      </p>
      <p
        className={`text-2xl font-black mt-1 ${colorMap[color] || colorMap.slate}`}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </Card>
  );
}

function formatMB(mb: number) {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
}

function utilizationColor(pct: number) {
  if (pct >= 95) return "bg-red-50 text-red-600";
  if (pct >= 80) return "bg-orange-50 text-orange-600";
  if (pct >= 60) return "bg-yellow-50 text-yellow-600";
  return "bg-green-50 text-green-600";
}

export default function StorageManagementPage() {
  const [page, setPage] = useState(1);
  const limit = 50;
  const queryClient = useQueryClient();

  // Storage overview
  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ["storage-overview"],
    queryFn: () => monitoringService.getStorageOverview(),
    refetchInterval: () => backgroundDbWorkRefetchIntervalMs(60_000),
  });

  // Storage grid
  const { data: gridData, isLoading } = useQuery({
    queryKey: ["storage-grid", page],
    queryFn: () => monitoringService.getStorageGrid(page, limit),
  });

  // Trigger audit mutation
  const auditMutation = useMutation({
    mutationFn: () => monitoringService.triggerStorageAudit(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storage-overview"] });
      queryClient.invalidateQueries({ queryKey: ["storage-grid"] });
    },
  });

  const grid = gridData?.data || gridData || [];
  const total = gridData?.total || grid.length;
  const hasAuditData = overview?.auditedTenants > 0;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Storage Management"
        subtitle="Monitor storage utilization across all tenants"
        actions={
          <button
            onClick={() => auditMutation.mutate()}
            disabled={auditMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {auditMutation.isPending ? "Running Audit..." : "Run Storage Audit"}
          </button>
        }
      />

      {auditMutation.isSuccess && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
          Storage audit completed: {auditMutation.data?.tenantsAudited ?? 0}{" "}
          tenants audited, {auditMutation.data?.warnings ?? 0} warnings.
        </div>
      )}

      {!overviewLoading && !hasAuditData && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
          No storage audit data available yet. Click{" "}
          <strong>Run Storage Audit</strong> above to compute storage usage for
          all tenants. The audit also runs automatically every 6 hours.
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Tenants"
          value={overview?.totalTenants ?? "—"}
          sub={
            overview && overview.auditedTenants < overview.totalTenants
              ? `${overview.auditedTenants} audited`
              : undefined
          }
          color="blue"
        />
        <StatCard
          label="Total Storage"
          value={
            overview?.totalStorageGB != null
              ? `${overview.totalStorageGB} GB`
              : "—"
          }
          color="slate"
        />
        <StatCard
          label="High Utilization (>80%)"
          value={overview?.highUtilization ?? "—"}
          color="orange"
        />
        <StatCard
          label="Critical (>95%)"
          value={overview?.criticalUtilization ?? "—"}
          color="red"
        />
      </div>

      {/* Storage Grid */}
      <Card>
        <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-4">
          Tenant Storage Usage
        </h3>
        {isLoading ? (
          <div className="animate-pulse space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-slate-100 rounded" />
            ))}
          </div>
        ) : grid.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">
            No active tenants found
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-left">
                    <th className="py-2 font-semibold text-slate-500">
                      Tenant
                    </th>
                    <th className="py-2 font-semibold text-slate-500">
                      Total Storage
                    </th>
                    <th className="py-2 font-semibold text-slate-500">
                      DB Size
                    </th>
                    <th className="py-2 font-semibold text-slate-500">Files</th>
                    <th className="py-2 font-semibold text-slate-500">Quota</th>
                    <th className="py-2 font-semibold text-slate-500">
                      Utilization
                    </th>
                    <th className="py-2 font-semibold text-slate-500">
                      Days Until Full
                    </th>
                    <th className="py-2 font-semibold text-slate-500">
                      Enforcement
                    </th>
                    <th className="py-2 font-semibold text-slate-500">
                      Audited
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {grid.map((tenant: any) => {
                    const utilPct = tenant.utilizationPercent;
                    const isAudited =
                      tenant.audited !== false && utilPct != null;
                    return (
                      <tr key={tenant.tenantId} className="hover:bg-slate-50">
                        <td className="py-2.5">
                          <Link
                            to={`/tenants/${tenant.tenantId}`}
                            className="font-medium text-blue-600 hover:underline"
                          >
                            {tenant.tenantName || tenant.tenantId}
                          </Link>
                          {tenant.planName && (
                            <p className="text-xs text-slate-400">
                              {tenant.planName}
                            </p>
                          )}
                        </td>
                        <td className="py-2.5 text-slate-600">
                          {tenant.totalStorageMB != null
                            ? formatMB(tenant.totalStorageMB)
                            : "—"}
                        </td>
                        <td className="py-2.5 text-slate-600">
                          {tenant.databaseSizeMB != null
                            ? formatMB(tenant.databaseSizeMB)
                            : "—"}
                        </td>
                        <td className="py-2.5 text-slate-600">
                          {tenant.fileStorageMB != null
                            ? formatMB(tenant.fileStorageMB)
                            : "—"}
                        </td>
                        <td className="py-2.5 text-slate-600">
                          {tenant.quotaLimitMB
                            ? formatMB(tenant.quotaLimitMB)
                            : tenant.quotaGB
                              ? `${tenant.quotaGB} GB`
                              : "—"}
                        </td>
                        <td className="py-2.5">
                          {isAudited ? (
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${utilizationColor(Number(utilPct))}`}
                            >
                              {Number(utilPct).toFixed(1)}%
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-400">
                              Not audited
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 text-xs text-slate-500">
                          {tenant.daysUntilFull != null
                            ? `${tenant.daysUntilFull}d`
                            : "—"}
                        </td>
                        <td className="py-2.5 text-xs">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold ${tenant.isOverQuota ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}
                          >
                            {tenant.isOverQuota ? "Blocked" : "Within quota"}
                          </span>
                        </td>
                        <td className="py-2.5 text-xs text-slate-500">
                          {tenant.computedAt
                            ? new Date(tenant.computedAt).toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {total > limit && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                <span className="text-xs text-slate-500">
                  Page {page} of {Math.ceil(total / limit)}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(total / limit)}
                    className="px-3 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
